import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Select } from './UI';
import { CompanyProfile, TaxObligation } from '../types';
import { api } from '../services/api';
import { accountLabel, daysBetween, formatDay, formatMoney, todayISO } from '../utils/format';

// Погашение налога: оплатить со счёта (создаётся пополнение ЕНС / платёж в СФР) или зачесть из сальдо ЕНС
export const SettleDialog: React.FC<{
    obligation: TaxObligation | null;
    org?: CompanyProfile;
    ensWallet?: number;  // не передан — берётся из отчёта организации обязательства
    onClose: () => void;
    onDone: () => void;
}> = ({ obligation, org, ensWallet: givenWallet, onClose, onDone }) => {
    const [loadedWallet, setLoadedWallet] = useState(0);
    useEffect(() => {
        if (!obligation || givenWallet !== undefined) return;
        api.getReport(obligation.organizationId, new Date().getFullYear())
            .then(r => setLoadedWallet(r.obligations.ens_wallet)).catch(() => setLoadedWallet(0));
    }, [obligation, givenWallet]);
    const ensWallet = givenWallet ?? loadedWallet;
    const sfr = obligation?.type === 'INJURIES';
    const [method, setMethod] = useState<'BANK' | 'ENS'>('BANK');
    const [date, setDate] = useState(todayISO());
    const [accountId, setAccountId] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!obligation) return;
        const canEns = !sfr && ensWallet >= obligation.amount;
        setMethod(canEns ? 'ENS' : 'BANK');
        setDate(todayISO());
        setAccountId(org?.activeAccountId || org?.accounts[0]?.id || '');
        setError('');
    }, [obligation, sfr, ensWallet, org]);

    if (!obligation) return null;
    const late = daysBetween(obligation.dueDate, date);

    const submit = async () => {
        setBusy(true); setError('');
        try {
            await api.settleObligation(obligation.id, { method, date, bankAccountId: method === 'BANK' ? accountId : null });
            onDone();
            onClose();
        } catch (e: any) {
            setError(e.message || 'Не удалось провести');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={!!obligation} onClose={onClose} title="Оплата налога">
            <div className="space-y-4 text-sm">
                <div className="bg-slate-50 rounded p-3">
                    <div className="font-bold">{obligation.description}</div>
                    <div className="flex justify-between mt-1"><span>Сумма</span><strong>{formatMoney(obligation.amount)}</strong></div>
                    <div className="flex justify-between text-slate-500"><span>Срок</span><span>{formatDay(obligation.dueDate)}</span></div>
                </div>

                <label className={`flex gap-2 items-start p-2 rounded border cursor-pointer ${method === 'BANK' ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
                    <input type="radio" checked={method === 'BANK'} onChange={() => setMethod('BANK')} className="mt-1" />
                    <div className="flex-1">
                        <div className="font-medium">Оплатить с расчётного счёта</div>
                        <div className="text-xs text-slate-500">
                            {sfr ? 'Создастся платёж в СФР на эту сумму.' : 'Создастся перевод на ЕНС на эту сумму и сразу зачёт.'}
                        </div>
                        {method === 'BANK' && (org?.accounts.length ?? 0) > 1 && (
                            <div className="mt-2"><Select value={accountId} onChange={e => setAccountId(e.target.value)}
                                options={(org?.accounts || []).map(a => ({ value: a.id, label: accountLabel(a) }))} /></div>
                        )}
                    </div>
                </label>

                <label className={`flex gap-2 items-start p-2 rounded border ${sfr ? 'opacity-50' : 'cursor-pointer'} ${method === 'ENS' ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
                    <input type="radio" disabled={sfr} checked={method === 'ENS'} onChange={() => setMethod('ENS')} className="mt-1" />
                    <div>
                        <div className="font-medium">Зачесть из сальдо ЕНС</div>
                        <div className="text-xs text-slate-500">
                            {sfr ? 'Травматизм платится в СФР отдельно, не через ЕНС.'
                                 : `На ЕНС сейчас ${formatMoney(ensWallet)}${ensWallet < obligation.amount ? ' — не хватает' : ''}.`}
                        </div>
                    </div>
                </label>

                <Input label="Дата оплаты" type="date" value={date} onChange={e => setDate(e.target.value)} />
                {late > 0 && <p className="text-xs text-amber-700">Оплата после срока: опоздание {late} дн.</p>}
                {error && <p className="text-xs text-red-600">{error}</p>}

                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button size="sm" onClick={submit} disabled={busy}>Провести</Button>
                </div>
            </div>
        </Modal>
    );
};
